import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  CardPurseIcon,
  CardTypesEnum,
  Inscription,
  List,
  Option,
  OptionKey,
  OverflowText,
  Preloader,
  SelectDropdown,
  Text,
  WarningCard,
  WarningIcon,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import AppButton from '@/components/controls/AppButton';
import theme from '@/styles/theme';

import { CardPurseAccess } from '../ManageAccountAccessModal.types';

const CARD_PURSE_ICON_SIZE = '32px';

const LockReasonWrap = styled(Box)`
  [data-testid$='_error-message'] {
    display: none;
  }
`;

interface Props {
  purses: CardPurseAccess[];
  lockReasons: Record<string, OptionKey>;
  lockReasonOptions: Option[];
  isLoading?: boolean;
  onSetLockReason: (id: string, reason: OptionKey) => void;
  onBack: () => void;
  onConfirm: () => void;
}

export const ConfirmAccountLock: React.FC<Props> = ({
  purses,
  lockReasons,
  lockReasonOptions,
  isLoading,
  onSetLockReason,
  onBack,
  onConfirm,
}) => {
  const [showErrors, setShowErrors] = useState(false);

  const isValid = useMemo(
    () => purses.every((purse) => !!lockReasons[purse.id]),
    [purses, lockReasons]
  );

  const handleConfirm = useCallback(() => {
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    onConfirm();
  }, [isValid, onConfirm]);

  return (
    <Box direction="column" data-testid="confirm-account-lock">
      <Box round="moduleRound" pad="l" background="module">
        <Text color="textBody" weight="bold" margin={{ bottom: 'xxs' }}>Accounts to be locked:</Text>
        <List>
          {purses.map((purse) => (
            <List.Item
              key={purse.id}
              direction="row"
              height="66px"
              data-testid={`confirm-account-lock_item_${purse.id}`}
              border={{ color: 'dangerBorder', size: 'small' }}
              background="dangerContainer"
              round="button1Round"
              align="center"
              pad={{ horizontal: 'spacing24', vertical: 'spacing16' }}
              style={{ cursor: 'initial' }}
            >
              <Box direction="row" align="center" gap="s" width="50%" margin={{ right: 's' }}>
                <CardPurseIcon type={purse.accountType as CardTypesEnum} size={CARD_PURSE_ICON_SIZE} />
                <Box style={{ width: '214px' }}>
                  <OverflowText
                    testId={`confirm-account-lock_item_${purse.id}_account-name`}
                    title={purse.accountName}
                    textStyle={{ fontWeight: 'bold', color: theme.colors.textBody }}
                  />
                </Box>
              </Box>
              <LockReasonWrap width="320px">
                <SelectDropdown
                  id={`lockReason-${purse.id}`}
                  testId={`confirm-account-lock_item_${purse.id}_lock-reason`}
                  name={`lockReason-${purse.id}`}
                  placeholder="Select lock reason"
                  singleMode
                  options={lockReasonOptions}
                  value={lockReasons[purse.id] || ''}
                  errorText={showErrors && !lockReasons[purse.id] ? 'This field is required' : undefined}
                  onChange={(value) => onSetLockReason(purse.id, value)}
                />
              </LockReasonWrap>
            </List.Item>
          ))}
        </List>
        <Box margin={{ top: 'm' }}>
          <WarningCard
            icon={<WarningIcon />}
            title={(
              <Inscription weight="bold" lineHeight="20px" color="textBody">
                The account(s) selected will be locked for the primary card as well as any dependent cards.
              </Inscription>
            )}
            topBorderColor="transparent"
            containerProps={{ height: '72px', pad: { horizontal: 'spacing24', vertical: 'spacing16' }, round: 'container2Round' }}
          />
        </Box>
      </Box>
      {showErrors && !isValid && (
        <Box margin={{ top: 'l' }} data-testid="confirm-account-lock_error-message">
          <Inscription color="danger" size="medium" weight={400} lineHeight="20px" textAlign="end">
            Lock reason selection is required
          </Inscription>
        </Box>
      )}
      <Box
        direction="row"
        fill="horizontal"
        margin={{ top: 's' }}
        align="center"
        justify="end"
        gap="s"
      >
        <AppButton
          buttonType="secondary"
          width="140px"
          type="button"
          testId="confirm-account-lock_back-button"
          onClick={onBack}
        >
          Back
        </AppButton>
        <AppButton
          disabled={isLoading}
          type="button"
          width="140px"
          testId="confirm-account-lock_confirm-button"
          onClick={handleConfirm}
        >
          {isLoading ? <Preloader color="white" /> : 'Yes, Lock'}
        </AppButton>
      </Box>
    </Box>
  );
};
